import { type ComponentType, useCallback, useEffect, useState } from 'react'
import type { FormulaPanelProps } from './FormulaPanel.js'

/**
 * Het formulepaneel komt pas als iemand het nodig heeft (R-14).
 *
 * **Waarom apart.** Het paneel trekt MathLive en tex2typst mee, samen ongeveer 870 kB van een
 * bundel van 3,05 MB (gemeten op 31 oktober 2026 met de bronkaart van `bouw-pagina`). Wie een
 * formulier opent zonder een formule te maken, en dat is bijna iedereen, betaalde die voor het
 * downloaden en het uitvoeren van het script bij elke eerste opening. Wat in het document staat
 * verandert niet: de knopen `inlineMath` en `blockMath` blijven in het schema
 * (`richtext/schema.ts`, valkuil 3) en renderen met KaTeX, los van dit paneel.
 *
 * **Geen `React.lazy`.** Een `lazy` die faalt (een chunk die niet aankomt) gooit naar de
 * dichtstbijzijnde foutgrens, en dat is hier het formulier; en hij onthoudt de fout voor de rest
 * van de sessie. Hier faalt alleen het paneel: de editor meldt het bij het veld, met een knop om
 * het opnieuw te proberen en een om te annuleren.
 *
 * **Vooraf halen bij de focus.** `preloadFormulaPanel` draait als de focus in een bewerkbaar
 * rich-text-veld komt, en dat is ruim voordat iemand op de formuleknop drukt; dan staat het paneel
 * er bij de klik meteen. Niet bij het openen van de pagina: dan concurreert het met de eerste
 * synchronisatie.
 */

type Panel = ComponentType<FormulaPanelProps>

export interface FormulaPanelLoader {
  /** Haalt het paneel, één keer per pagina; een mislukte poging mag opnieuw. */
  readonly preload: () => Promise<Panel>
  /**
   * Het paneel als het er is, en of het halen mislukte, voor een paneel dat nu open moet.
   *
   * Staat het paneel er al (een eerdere opening of het vooraf halen), dan is het er bij de eerste
   * render, zonder een tussenstand.
   */
  readonly usePanel: (open: boolean) => { readonly Panel: Panel | undefined; readonly failed: boolean; readonly retry: () => void }
}

/**
 * Een lader om één import heen. De pagina gebruikt die hieronder; een test geeft een import die
 * faalt, want een chunk die niet aankomt is in een browsertest niet anders na te spelen.
 */
export function createFormulaPanelLoader(load: () => Promise<{ readonly FormulaPanel: Panel }>): FormulaPanelLoader {
  let loaded: Panel | undefined
  let pending: Promise<Panel> | undefined

  function preload(): Promise<Panel> {
    if (loaded !== undefined) return Promise.resolve(loaded)
    pending ??= load().then(
      (module) => {
        loaded = module.FormulaPanel
        return loaded
      },
      (error: unknown) => {
        pending = undefined
        throw error
      },
    )
    return pending
  }

  function usePanel(open: boolean): { readonly Panel: Panel | undefined; readonly failed: boolean; readonly retry: () => void } {
    const [panel, setPanel] = useState<Panel | undefined>(() => loaded)
    const [failed, setFailed] = useState(false)
    const [attempt, setAttempt] = useState(0)

    useEffect(() => {
      if (!open) {
        setFailed(false)
        return
      }
      if ((panel ?? loaded) !== undefined) return
      let current = true
      preload().then(
        (found) => {
          if (current) setPanel(() => found)
        },
        (error: unknown) => {
          // De volledige keten in het log (ontwerp 11.4); de invuller krijgt een zin bij het veld.
          console.error('het formulepaneel kon niet worden geladen', error)
          if (current) setFailed(true)
        },
      )
      return () => {
        current = false
      }
    }, [open, panel, attempt])

    const retry = useCallback(() => {
      setFailed(false)
      setAttempt((n) => n + 1)
    }, [])

    // `loaded` ook als deze editor monteerde vóór het halen klaar was: dan staat het paneel er
    // bij de eerste render na de klik, zonder een render ertussen.
    return { Panel: panel ?? loaded, failed, retry }
  }

  return { preload, usePanel }
}

const formulaPanel = createFormulaPanelLoader(() => import('./FormulaPanel.js'))

export const preloadFormulaPanel = formulaPanel.preload
export const useFormulaPanel = formulaPanel.usePanel
